import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { User, Eye, Trophy, Camera, Palette, Lock } from "lucide-react";

interface CharacterCreationProps {
  onComplete: () => void;
}

export default function CharacterCreation({ onComplete }: CharacterCreationProps) {
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateCharacter = async () => {
    setIsCreating(true);
    // Simulate character creation process
    setTimeout(() => {
      onComplete();
    }, 1500);
  };

  return (
    <div className="min-h-screen flex flex-col relative bg-gradient-to-b from-dark-base to-dark-elevated text-white">
      <header className="relative z-10 p-6 text-center">
        <h1 className="text-2xl font-bold mb-2" data-testid="heading-create-artist">Create Your Artist</h1>
        <p className="text-gray-300" data-testid="subtitle-customize">Customize your street art explorer</p>
      </header>
      
      <main className="relative z-10 flex-1 px-6 pb-8">
        <Card className="bg-dark-elevated border-gray-700">
          <CardContent className="pt-6">
            
            {/* Avatar Selection */}
            <div className="text-center mb-6">
              <div className="w-24 h-24 bg-gradient-to-br from-neon-green to-teal-custom rounded-full mx-auto mb-4 flex items-center justify-center" data-testid="avatar-display">
                <User className="text-2xl text-dark-base" size={32} />
              </div>
              <Button 
                variant="link" 
                className="text-teal-custom hover:text-teal-custom/80 text-sm"
                data-testid="button-choose-avatar"
              >
                <Camera className="mr-1" size={16} />
                Choose Avatar
              </Button>
            </div>
            
            {/* Character Stats */}
            <div className="grid grid-cols-2 gap-4 mb-6" data-testid="character-stats">
              <Card className="bg-dark-surface">
                <CardContent className="pt-4 text-center">
                  <Eye className="text-coral mx-auto mb-2" size={20} />
                  <div className="text-2xl font-bold" data-testid="stat-discoveries">0</div>
                  <div className="text-xs text-gray-400">Discoveries</div>
                </CardContent>
              </Card>
              <Card className="bg-dark-surface">
                <CardContent className="pt-4 text-center">
                  <Trophy className="text-yellow-400 mx-auto mb-2" size={20} />
                  <div className="text-2xl font-bold" data-testid="stat-level">1</div>
                  <div className="text-xs text-gray-400">Level</div>
                </CardContent>
              </Card>
            </div>
            
            {/* NFT Collection Starter Pack */}
            <Card className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border-purple-500/30 mb-6">
              <CardContent className="pt-4">
                <div className="flex items-center mb-3">
                  <div className="text-purple-400 mr-2">💎</div>
                  <span className="font-semibold" data-testid="nft-collection-title">Starter NFT Collection</span>
                </div>
                <div className="grid grid-cols-3 gap-2" data-testid="starter-nfts">
                  <Card className="bg-dark-surface">
                    <CardContent className="pt-2 text-center">
                      <div className="text-neon-green mb-1">🎨</div>
                      <div className="text-xs">Spray Can</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-dark-surface">
                    <CardContent className="pt-2 text-center">
                      <Palette className="text-coral mx-auto mb-1" size={16} />
                      <div className="text-xs">Palette</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-dark-surface opacity-50">
                    <CardContent className="pt-2 text-center">
                      <Lock className="text-gray-500 mx-auto mb-1" size={16} />
                      <div className="text-xs">Locked</div>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
            
            <Button 
              onClick={handleCreateCharacter}
              disabled={isCreating}
              className="w-full bg-neon-green text-dark-base font-semibold hover:bg-neon-green/90 glow-effect"
              data-testid="button-enter-streetscope"
            >
              {isCreating ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-dark-base border-t-transparent rounded-full mr-2" />
                  Creating Character...
                </>
              ) : (
                'Enter StreetScope'
              )}
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
